import React, { useEffect, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import toast from "react-hot-toast";

import FieldCreateEditCard from "./FieldCreateEditCard";
import { updateCard } from "../../../../services/apiCards";
import { truncateText } from "../../../../utils";
import { Tooltip } from "@mui/material";
import useToastPromise from "../../../../hooks/useToast";
import useBoardIdByKey from "../../../../hooks/useBoardIdByKey";

function EditTitle({
  project,
  isEditing,
  callback,
  setIsEditing,
  subtask = false,
  isCardTitle = false,
}) {
  const { id } = useBoardIdByKey();
  const queryClient = useQueryClient();
  const [value, setValue] = useState(project?.card?.title);
  const { showToastPromise } = useToastPromise();

  useEffect(() => {
    // Only update value if we're not currently editing to avoid overwriting user input
    if (!isEditing) {
      setValue(project?.card?.title);
    }
  }, [project?.card?.title, isEditing]);


  const closeEditing = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      setIsEditing(false);
      updateCardAPICall();
    }
    if (e.key === "btnClicked") {
      setIsEditing(false);
      updateCardAPICall();
    }
  };
  const onBlur = () => {
    setIsEditing(false);
    updateCardAPICall();
  };

  const { mutate } = useMutation({
    mutationFn: async (data) => {
      return await showToastPromise(updateCard, data);
    },
    onMutate: async () => {
      // Optimistic update: Update the card title immediately in cache
      if (id) {
        await queryClient.cancelQueries({ queryKey: [`cards`, id] });
        const previousCards = queryClient.getQueryData([`cards`, id]);
        
        // Optimistically update the card title - use correct data structure
        queryClient.setQueryData([`cards`, id], (oldData) => {
          if (!oldData?.data?.data?.responseData) return oldData;
          return {
            ...oldData,
            data: {
              ...oldData.data,
              data: {
                ...oldData.data.data,
                responseData: oldData.data.data.responseData.map((card) => {
                  if (card?.card?.card_id === project?.card?.card_id) {
                    return {
                      ...card,
                      card: {
                        ...card.card,
                        title: value,
                      },
                    };
                  }
                  return card;
                }),
              },
            },
          };
        });
        
        return { previousCards };
      } else {
        await queryClient.cancelQueries({ queryKey: [`mineCards`] });
        const previousCards = queryClient.getQueryData([`mineCards`]);
        
        // Optimistically update for mineCards - use correct data structure
        queryClient.setQueryData([`mineCards`], (oldData) => {
          if (!oldData?.data?.data?.responseData) return oldData;
          return {
            ...oldData,
            data: {
              ...oldData.data,
              data: {
                ...oldData.data.data,
                responseData: oldData.data.data.responseData.map((card) => {
                  if (card?.card?.card_id === project?.card?.card_id) {
                    return {
                      ...card,
                      card: {
                        ...card.card,
                        title: value,
                      },
                    };
                  }
                  return card;
                }),
              },
            },
          };
        });
        
        return { previousCards };
      }
    },
    onSuccess: (res, variables, context) => {
      // Invalidate subtasks query
      queryClient.invalidateQueries({
        queryKey: [`subTasks`],
      });
      
      // Refetch cards after a delay to get fresh data from server
      // This ensures the optimistic update stays visible until server confirms
      setTimeout(() => {
        if (id) {
          queryClient.invalidateQueries({
            queryKey: [`cards`, id],
          });
        } else {
          queryClient.invalidateQueries({
            queryKey: [`mineCards`],
          });
        }
      }, 300); // Small delay to ensure optimistic update is visible
      
      callback?.(value);
    },
    onError: (error, variables, context) => {
      // Rollback optimistic update on error
      if (context?.previousCards) {
        if (id) {
          queryClient.setQueryData([`cards`, id], context.previousCards);
        } else {
          queryClient.setQueryData([`mineCards`], context.previousCards);
        }
      }
    },
  });

  const updateCardAPICall = () => {
    if (value != project?.card?.title) {
      mutate({
        card_id: project?.card?.card_id,
        data: { title: value },
      });
    }
  };

  

  return (
    <>
      {isEditing ? (
        <FieldCreateEditCard
          value={value}
          setCreateBtnOpen={onBlur}
          handleKeyDown={closeEditing}
          edit={true}
          setTitle={(val) => setValue(val)}
          minRows={1}
          className={"edit-card-textarea"}
        />
      ) : (
        <>
          {subtask ? (
            <p className="card-title-text" title={project?.card?.title}>
              {truncateText(project?.card?.title, 60)}
            </p>
          ) : (
            <p className="card-title-text">
              {isCardTitle
                ? project?.card?.title
                : truncateText(project?.card?.title, 60)}
            </p>
          )}
        </>
      )}
    </>
  );
}

export default EditTitle;
